import { ATTR_NAME, C8AttributeId, C8NodeType, cnl, constructDataStoreIdFromComponents, DataStoreId, DataStorePrefixes, getDataStoreComponents, IAggregateStoreAlternativeInfo, IAggregateStoreDefinitionInfo, IContextualDataStoreId, ICustomViewFilter, ICustomViewFilterSettings, IDataStoreInfo, IRelationDataStoreId, isGlobalDataStore, ISubtypeDataStoreId, Operator, queryDataStores, VALUE_TYPE } from "c8-platform";
import comm from "csi/comm";
import ExtendedPath from "csi/core/ExtendedPath";
import RuleStore from "csi/middleware/attributeRule/RuleStore";
import { createRuleStore } from "csi/middleware/customView/customViewDataManagementService";
import { writeCustomView } from "csi/middleware/customView/customViewSaveService";
import { getCustomViewContainerName, getSingularPathsFromSource } from "csi/middleware/customView/customViewService";
import { ICustomViewQueryOptions } from "csi/middleware/customView/options/CustomViewOptions";
import { focusOnFirstInvalidInputField } from "csi/middleware/form/formUtility";
import { getConfigLocaleProvider } from "csi/page/configLocaleProvider";
import { getClass, getTypeAttribute } from "csi/reflection/reflection";
import siteDefaultLocale from "csi/siteDefaultLocale";
import { pathCreate } from "csi/utility/pathUtility";
import xml from "csi/xml";
import Writer from "csi/xml/Writer";
import { ILocaleProvider } from "designSystem/locale";
import { cloneDeep, forEach, groupBy, isEmpty, isEqual, sortBy } from "lodash";
import { action, computed, IObservableArray, makeObservable, observable, runInAction } from "mobx";
import { ChangeEventHandler, createContext, RefObject, useContext } from "react";
import AggregateStoreAlternativeStore from "./AggregateStoreAlternativeStore";
import AggregateStoreGroupStore from "./AggregateStoreGroupStore";

const TYPE_ATTRIBUTE_AGGREGATE_STORE_DEFINITION = "AttributeAggregateStoreDefinition";

interface IAggregateStoreInfoValue {
	/**
	 * aggregate store alternatives - conditional aggregate stores will have multiple, while simple aggregate stores will just have one;
	 * if there is no OOTB definition, then this will be undefined
	 */
	alternatives?: IAggregateStoreAlternativeInfo[];

	/** custom alternatives defined by customer if overridden; undefined if not overridden by the customer */
	customAlternatives?: IAggregateStoreAlternativeInfo[];

	/** mapping of data store id to query settings */
	dataStoreQueryMap?: Record<string, ICustomViewFilter>;
}

/** return name for given data store id - English only since data store names are not localized */
function getDataStoreName(dataStoreId: DataStoreId, loc: ILocaleProvider): string {
	const components = getDataStoreComponents(dataStoreId);
	if (!components) {
		return "";
	}

	const { relation } = components as IRelationDataStoreId;
	const { custom, name, sourceClass } = components as IContextualDataStoreId;
	const { onlyActiveSubtypes, subtypedClass } = components as ISubtypeDataStoreId;

	if (subtypedClass) {
		// subtype Data Stores
		return onlyActiveSubtypes ? "Active" : "All";
	} else if (relation) {
		// Relation Data Stores
		return `Relation: ${loc.attributeResource(sourceClass, relation)}`;
	} else if (custom) {
		// custom Contextual or Global Data Stores
		return `Custom: ${name}`;
	} else {
		// OOTB Contextual or Global Data Stores
		return name || "All";
	}
}

/** return unique key for given type and path */
function getTypePathKey(type: C8NodeType, extendedPath: ExtendedPath | undefined): string {
	return `${type}|${extendedPath?.value || ""}`;
}

export interface IDataStoreOption {
	/** data store info */
	dataStoreId: DataStoreId;

	/** data store info */
	dataStoreInfo: IDataStoreInfo;

	/** path name to group by */
	group: string;
	
	/** data store id */
	id: string;
	
	/** name of item */
	name: string;

	/** path to source of data store */
	pathToDataStore: string;
}

export class AggregateStoreInfoStore {
	/** initial value for custom alternatives so that we can detect any changes */
	readonly customAlternatives: IObservableArray<AggregateStoreAlternativeStore>;
	
	/** aggregate store definition from server */
	readonly definition: IAggregateStoreDefinitionInfo;
	
	/** initial value of dialog */
	readonly initialInfoValue: IAggregateStoreInfoValue;

	/** store is initially empty */
	readonly isInitiallyEmpty: boolean;

	/** locale provider */
	readonly loc: ILocaleProvider;

	/** map of data store options per source path */
	readonly mapDataStoreOptions = observable.map<string, IDataStoreOption[]>(null, { deep: false });
	
	/** initial value for OOTB alternatives so that we can detect any changes */
	readonly ootbAlternatives: IObservableArray<AggregateStoreAlternativeStore>;
	
	/** indicates if using custom aggregate store */
	@observable overridden: boolean;

	/** attribute which will use aggregate store */
	readonly relationId: C8AttributeId;

	/** map of rule stores by data store id */
	readonly ruleStoreMap = observable.map<DataStoreId, RuleStore>();

	/** indicates if required inputs should be validated (typically only after Save clicked) */
	@observable private validateRequired = false;

	/** form ref */
	formRef: RefObject<HTMLFormElement>;

	/** set form ref */
	setFormRef = (ref: RefObject<HTMLFormElement>): void => {
		this.formRef = ref;
	};

	/** list of alternatives whether or not using an OOTB or custom aggregate store */
	get alternatives(): AggregateStoreAlternativeStore[] {
		const { customAlternatives, ootbAlternatives, overridden } = this;
		return overridden ? customAlternatives : ootbAlternatives;
	}
	
	/** indicates if all data entered for custom alternatives is valid so that a new alternative can be entered */
	get canAddAlternative(): boolean {
		const { customAlternatives, overridden } = this;
		return overridden && customAlternatives.every((alternative) => alternative.isValid);
	}

	/** indicates if an OOTB aggregate store exists and can be overridden */
	get canOverrideAggregateStore(): boolean {
		const { definition, relationId, sourceClass } = this;
		const ra = getTypeAttribute(sourceClass, relationId);

		// for now, do not allow fully overriding an aggregate store with an aggregate class (for composite relations)
		return !!definition.alternatives && !ra?.isComposite;
	}

	/** locale provider for site locale for existing resources */
	@computed({ keepAlive: true }) get baseConfigLocaleProvider(): ILocaleProvider {
		const resolver = siteDefaultLocale.getBaseConfigResolver();
		return getConfigLocaleProvider(resolver);
	}

	/** locale provider for site locale for edited resources */
	@computed({ keepAlive: true }) get configLocaleProvider(): ILocaleProvider {
		const resolver = siteDefaultLocale.getConfigResolver();
		return getConfigLocaleProvider(resolver);
	}

	/** indicates if any changes have been made to aggregate store */
	get disableSave(): boolean {
		const { infoValue, initialInfoValue, isInitiallyEmpty } = this;
		return !isInitiallyEmpty && isEqual(infoValue, initialInfoValue);
	}

	/** indicates if aggregate store is dynamic and configuration cannot be determined */
	get dynamic(): boolean {
		return !!this.alternatives[0]?.dynamic;
	}

	/** indicates if target class of aggregate store is abstract */
	get hasAbstractTargetClass(): boolean {
		const rc = getClass(this.targetClass);
		return rc.abstract;
	}

	/** value representing all editable settings in store */
	@computed get infoValue(): IAggregateStoreInfoValue {
		const { overridden, ruleStoreMap } = this;
		const dataStoreQueryMap: Record<DataStoreId, ICustomViewFilter> = {};
		const alternatives = overridden ? undefined : this.alternatives?.map((store) => store.alternativeValue);
		const customAlternatives = overridden ? this.customAlternatives?.map((store) => store.alternativeValue) : undefined;

		ruleStoreMap.forEach((ruleStore, dataStoreId) => {
			if (ruleStore?.currentFilterSettingsWithoutEmptyFilters) {
				dataStoreQueryMap[dataStoreId] = ruleStore.currentFilterSettingsWithoutEmptyFilters;
			}
		});

		return {
			alternatives,
			customAlternatives,
			dataStoreQueryMap,
		};
	}

	/** indicates if aggregate store has more than one alternative */
	get isConditionalAggregateStore(): boolean {
		return this.alternatives.length > 1;
	}

	/** indicates if aggregate store data is read-only */
	get readOnly(): boolean {
		return !this.dynamic && !this.overridden;
	}

	/** error message to use when value is required  */
	get requiredValueError(): string | undefined {
		if (this.validateRequired) {
			return this.loc.resource("RequiredValue");
		}
	}

	/** indicates if no alternative information needs to be shown - just groups */
	get showGroupsOnly(): boolean {
		const { alternatives, dynamic, isConditionalAggregateStore, overridden } = this;
		const firstAlternative = alternatives[0];

		// if overridden, then alternative is editable and needs to be shown;
		// and if there are already multiple alternatives then they would need to be shown
		if (overridden || isConditionalAggregateStore) {
			return false;
		}

		// if dynamic, can only refine data stores;
		// otherwise, only show alternative information if something to show
		return dynamic || (!firstAlternative.description && !firstAlternative.condition);
	}

	/** source type for aggregate store */
	get sourceClass(): C8NodeType {
		return this.definition.sourceClass;
	}

	/** source path for aggregate store */
	get sourcePath(): ExtendedPath {
		return new ExtendedPath(this.definition.sourceClass);
	}

	/** source path for aggregate store */
	get sourceRelationDataStoreId(): DataStoreId {
		const { relationId, sourceClass, targetClass } = this;

		return constructDataStoreIdFromComponents({
			prefix: DataStorePrefixes.SOURCE,
			relation: relationId,
			sourceClass,
			target: targetClass,
		} as IRelationDataStoreId);
	}

	/** target type for aggregate store */
	get targetClass(): C8NodeType {
		return this.definition.targetClass;
	}

	/** return true if alternative is not the last alternative of many */
	alternativeRequiresCondition = (alternative: AggregateStoreAlternativeStore): boolean => {
		const { alternatives } = this;
		const index = alternatives.indexOf(alternative);
		return index < alternatives.length - 1;
	};

	/** clear map of data store options */
	@action clearDataStoreOptions = (): void => {
		this.mapDataStoreOptions.clear();
	};

	/** create option for given data store and path */
	createDataStoreOption = (dataStoreId: DataStoreId, dataStoreInfo: IDataStoreInfo, path?: ExtendedPath): IDataStoreOption => {
		const { getNameForPath, loc } = this;
		const global = isGlobalDataStore(dataStoreId);
		const group = global ? loc.resource("Global") : getNameForPath(path);
		const name = getDataStoreName(dataStoreId, loc);
		const pathValue = path?.value || "";

		return {
			dataStoreId,
			dataStoreInfo,
			group,
			id: `${dataStoreId}-${pathValue}`,
			name,
			pathToDataStore: pathValue,
		};
	};

	/** return true if given resource already exists */
	doesResourceExist = (res?: string): boolean => {
		return res && !!this.baseConfigLocaleProvider.resource(res);
	};

	/** return data store options for given target type and path if already queried */
	getDataStoreOptions = (targetClass: C8NodeType, path: ExtendedPath | undefined): IDataStoreOption[] | undefined => {
		const key = getTypePathKey(targetClass, path);
		return this.mapDataStoreOptions.get(key);
	};

	/** return name for given path */
	getNameForPath = (path: ExtendedPath): string => {
		return path ? getCustomViewContainerName(this.loc, path) : "";
	};

	/** return rule store for given data store id containing any existing filters from query map */
	@action getRuleStore = (dataStoreId: DataStoreId, existingFilters?: ICustomViewFilter): RuleStore => {
		const { definition, loc, ruleStoreMap } = this;
		
		if (!ruleStoreMap.has(dataStoreId)) {
			const { target } = getDataStoreComponents(dataStoreId);
			ruleStoreMap.set(dataStoreId, createRuleStore(target, definition.sourceClass, loc, existingFilters));
		}

		return ruleStoreMap.get(dataStoreId);
	};

	/** process change to override switch */
	@action onChangeOverride: ChangeEventHandler<HTMLInputElement> = async (): Promise<void> => {
		if (this.overridden) {
			this.overridden = false;
		} else {
			this.overridden = true;
			this.createCustomAlternative(true);
		}

		this.resetValidation();
	};
	
	/** add custom alternative */
	@action onClickAddAlternative = (): void => {
		this.createCustomAlternative();
		this.resetValidation();
	};

	/** query for data store options for source class and optional path */
	queryDataStoreOptions = (targetClass: C8NodeType, selectedPath?: ExtendedPath): void => {
		const {
			createDataStoreOption,
			getNameForPath,
			mapDataStoreOptions,
			relationId,
			sourcePath,
			sourceRelationDataStoreId,
		} = this;
		const key = getTypePathKey(targetClass, selectedPath);
		
		if (!mapDataStoreOptions.has(key)) {
			// collect all paths where to look for data stores
			const allPaths = [sourcePath, ...getSingularPathsFromSource(sourcePath, relationId)];
			const selectedValue = selectedPath?.value;
			const paths = selectedValue ? allPaths.filter((ep) => ep.value !== selectedValue) : allPaths;
			const sortedPaths = sortBy(paths, [getNameForPath]);
			const sourceTypes = sortedPaths.map((path) => path.endType);
			const setSourceTypes = new Set<string>(sourceTypes);

			// add group for global data stores
			sortedPaths.unshift(null);

			if (selectedPath) {
				sortedPaths.unshift(selectedPath);
				setSourceTypes.add(selectedPath.endType);
			}

			// query for data stores from source classes reachable by paths
			queryDataStores(targetClass, [...setSourceTypes], true, true).then((dataStores) => {
				const dataStoresBySource = groupBy(dataStores, (dataStore) => dataStore.sourceClass || ""); 

				// create options for each path/data store combination
				const options = sortedPaths.flatMap((path) => {
					const pathOptions = [];

					dataStoresBySource[path?.endType || ""]?.forEach(({ dataStoreId, info }) => {
						if (dataStoreId !== sourceRelationDataStoreId) {
							pathOptions.push(createDataStoreOption(dataStoreId, info, path));
						}
					});

					return sortBy(pathOptions, ["name"]);
				});

				runInAction(() => {
					mapDataStoreOptions.set(key, options);
				});
			});
		}
	};

	/** remove custom alternative from store */
	@action removeAlternative = (alternative: AggregateStoreAlternativeStore): void => {
		const { customAlternatives, _cleanupRuleStoreMap } = this;
		
		customAlternatives.remove(alternative);
		_cleanupRuleStoreMap();
	};
	
	/** remove group from parent alternative, and if dynamic then also remove any filters for the group */
	@action removeGroup = (group: AggregateStoreGroupStore): void => {
		group.alternative.groups.remove(group);
		this._cleanupRuleStoreMap();
	};

	/** reset validation in cases where new fields are added and don't want to be invalid from the start */
	@action resetValidation = (): void => {
		this.validateRequired = false;
	};

	/** resolve given resource, first checking edited resources and then updated resources (run through update config) */
	resolveConfigResource = (res: string): string | undefined => {
		const { baseConfigLocaleProvider, configLocaleProvider } = this;
		return configLocaleProvider.resource(res) || baseConfigLocaleProvider.resource(res);
	};

	/** validate that the store is ready to be saved */
	@action validate = (): boolean => {
		const { alternatives } = this;

		// from this point forward, input fields that are required and empty should show error messages
		this.validateRequired = true;

		const valid = alternatives.every((alternative) => alternative.isValid);

		if (!valid) {
			focusOnFirstInvalidInputField(this.formRef.current);
		}

		return valid;
	};

	/** write custom views to store data store queries */
	writeAggregateStoreDefinition = (writer: Writer, dataStoreQueryMap?: Record<DataStoreId, cnl>): cnl => {
		const { REF_MAP, STRING } = VALUE_TYPE;
		const { sourceClass, targetClass } = this;
		const definitionUrl = comm.createUrl();

		const cn = writer.createNode(definitionUrl, "AggregateStoreDefinition", null, xml.NODE_FLAG_NOEDIT)
			// NOTE: "Node Name", "Description" not yet populated on AggregateStoreDefinition
			// These are in the model, but not necessary unless allowing reuse of aggregate stores (i.e. for actions)
			// which would be a future epic
			.changeAttribute("SourceClass", STRING, sourceClass)
			.changeAttribute("TargetClass", STRING, targetClass);
		
		if (dataStoreQueryMap) {
			cn.changeAttributeKeys("DataStoreQueryMap", REF_MAP, dataStoreQueryMap);
		}

		return definitionUrl;
	};

	/** write custom views to store data store queries */
	writeDataStoreQueries = (writer: Writer): Record<DataStoreId, cnl> => {
		const { ruleStoreMap, targetClass } = this;
		const map: Record<DataStoreId, cnl> = {};

		ruleStoreMap.forEach((ruleStore, dataStoreId) => {
			if (ruleStore?.currentFilterSettingsWithoutEmptyFilters) {
				// create options with filter settings and create custom view
				const customViewUrl = comm.createUrl();
				const options: ICustomViewQueryOptions = {
					filterSettings: { 0: ruleStore.currentFilterSettingsWithoutEmptyFilters },
				};
					
				writeCustomView(writer, null, null, options, customViewUrl, targetClass, null, false);
				map[dataStoreId] = customViewUrl;
			}
		});

		return map;
	};

	/** write operations to save any changes */
	writeOperations = (writer: Writer, attributeId?: C8AttributeId): void => {
		const { BOOLEAN, EXPRESSION, REF, STRING } = VALUE_TYPE;
		const {
			customAlternatives,
			overridden,
			sourceClass,
			writeAggregateStoreDefinition,
			writeDataStoreQueries,
		} = this;
		let definitionUrl: cnl;

		if (attributeId) {
			// delete node that connects attribute to aggregate store
			const query = writer.createQuery();
			query.Type(TYPE_ATTRIBUTE_AGGREGATE_STORE_DEFINITION)
				.Attr(ATTR_NAME, Operator.EQUALS, attributeId)
				.Attr("SourceClass", Operator.EQUALS, sourceClass);

			// NOTE: for now, since we don't reuse custom aggregate stores, delete the definition too;
			// if we ever allow aggregate store definitions to be reused, then we should leave alone
			writer.deleteQuery(query, null, pathCreate("AttributeAggregateStore"));
			writer.deleteQuery(query);
		}

		if (overridden) {
			definitionUrl = writeAggregateStoreDefinition(writer);

			customAlternatives.forEach(({ condition, description, groups }) => {
				const alternativeUrl = comm.createUrl();

				writer.createNode(alternativeUrl, "AggregateStoreAlternative", description, xml.NODE_FLAG_NOEDIT)
					.changeAttribute("Condition", EXPRESSION, condition)
					.attach(definitionUrl, "Alternatives");
				
				groups.forEach(({ groupInfo, label }) => {
					const { dataStoreId, labelResource, path, suggested } = groupInfo;

					writer.createNode(null, "AggregateStoreGroup", labelResource, xml.NODE_FLAG_NOEDIT)
						.changeAttribute("DataStoreId", STRING, dataStoreId)
						.changeAttribute("Path", STRING, path)
						.changeAttribute("Suggested", BOOLEAN, suggested)
						.attach(alternativeUrl, "Groups");
					
					// write label to config resources
					if (label) {
						siteDefaultLocale.setResource(writer, siteDefaultLocale.ATTR_RESOURCES, [labelResource], label);
					}
				});
			});
		} else {
			// write operations for refining OOTB data store queries
			const dataStoreQueryMap = writeDataStoreQueries(writer);

			if (!isEmpty(dataStoreQueryMap)) {
				definitionUrl = writeAggregateStoreDefinition(writer, dataStoreQueryMap);
			}
		}

		if (definitionUrl && attributeId) {
			writer.createNode(null, TYPE_ATTRIBUTE_AGGREGATE_STORE_DEFINITION, attributeId, xml.NODE_FLAG_NOEDIT)
				.changeAttribute("SourceClass", STRING, sourceClass)
				.changeAttribute("AttributeAggregateStore", REF, definitionUrl);
		}
	};
	
	/** remove custom alternative from store */
	@action private _cleanupRuleStoreMap = (): void => {
		const { alternatives, customAlternatives, ruleStoreMap } = this;
		const setIds = new Set<string>();

		// collect all data store ids
		[...alternatives, ...customAlternatives].forEach(({ dataStoreIds }) => {
			dataStoreIds.forEach((id) => {
				setIds.add(id);
			});
		});
		
		// remove unused entries for rules
		for (const id of ruleStoreMap.keys()) {
			if (!setIds.has(id)) {
				ruleStoreMap.delete(id);
			}
		}
	};

	/** popuplate default alternative for new custom aggregate store */
	@action private createCustomAlternative = (onlyIfEmpty?: boolean): void => {
		const { customAlternatives, targetClass } = this;
		const { length } = customAlternatives;

		// nothing to do if there are already custom alternatives
		if (onlyIfEmpty && length) {
			return;
		}

		const alternativeInfo = {
			groups: [{
				nodeType: targetClass,
				notFirstOfType: !!length,
			}],
		};

		customAlternatives.push(new AggregateStoreAlternativeStore(alternativeInfo, this));
	};

	constructor(relationId: C8AttributeId, definition: IAggregateStoreDefinitionInfo, localeProvider: ILocaleProvider, dataStoreFilterMap?: Record<DataStoreId, ICustomViewFilterSettings>) {
		const {
			alternatives = [],
			customAlternatives = [],
		} = definition;

		this.definition = definition;
		this.loc = localeProvider;
		this.relationId = relationId;

		this.customAlternatives = observable.array<AggregateStoreAlternativeStore>(customAlternatives.map((alternative) => new AggregateStoreAlternativeStore(cloneDeep(alternative), this)));
		this.ootbAlternatives = observable.array<AggregateStoreAlternativeStore>(alternatives.map((alternative) => new AggregateStoreAlternativeStore(alternative, this)));
		
		// no OOTB definition, so create custom aggregate store
		if (!alternatives.length) {
			this.overridden = true;
			this.isInitiallyEmpty = !customAlternatives.length;
			this.createCustomAlternative(true);
		} else {
			this.overridden = !!customAlternatives.length;
		}

		// populate rule store map
		forEach(dataStoreFilterMap, (settings, dataStoreId) => {
			this.getRuleStore(dataStoreId, settings?.[0]);
		});

		// calculate and save this last since it represents all data in dialog
		this.initialInfoValue = this.infoValue;
		makeObservable(this);
	}
} 

export const AggregateStoreInfoStoreContext = createContext<AggregateStoreInfoStore>(undefined);
export const useAggregateStoreInfoStore = (): AggregateStoreInfoStore => useContext(AggregateStoreInfoStoreContext);